import clsx from 'clsx';
import { useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Icon, Link } from '@trg/tlw-ui-component';
import type { ServiceInfo } from '../../../api';
import { useLayoutStore } from '../../../../layout';

export interface ServiceItemProps {
  id: string;
  title: string;
  items: ServiceInfo[];
}

export interface ServiceActionsProps {
  activeMenu: string | null;
  changeMenu: (id: string) => void;
}

export function ServiceItem({
  id,
  title,
  items,
  activeMenu,
  changeMenu,
}: ServiceItemProps & ServiceActionsProps) {
  const isMobile = useLayoutStore.use.isMobile();

  const isOpen = useMemo(() => {
    return id === activeMenu;
  }, [id, activeMenu]);

  const elementRef = useRef<HTMLUListElement>(null);

  const handleEnter = () => {
    if (elementRef.current) {
      elementRef.current.style.height = '0';
    }
  };
  const handleEntering = () => {
    if (elementRef.current) {
      const h = elementRef.current.scrollHeight;
      elementRef.current.style.height = h + 'px';
    }
  };
  const handleExit = () => {
    if (elementRef.current) {
      elementRef.current.style.height = 'auto';
    }
  };
  const handleExiting = () => {
    if (elementRef.current) {
      elementRef.current.style.height = '0';
    }
  };

  return (
    <section
      className={clsx(
        'tlw-grid tlw-py-3 tlw-border-b-[0.5px] tlw-border-gray-500 tlw-duration-300 first:tlw-pt-0 md:tlw-block md:tlw-py-0 md:tlw-space-y-2 md:tlw-border-none',
        isOpen
          ? 'tlw-grid-rows-[24px_1fr] tlw-gap-2'
          : 'tlw-grid-rows-[24px_0fr] tlw-gap-0'
      )}
    >
      <div
        className={clsx(
          'tlw-text-txt-700 tlw-flex tlw-justify-between tlw-items-center tlw-cursor-pointer hover:tlw-text-txt-800 md:tlw-cursor-default',
          isOpen && 'tlw-text-txt-800'
        )}
        onClick={() => changeMenu(id)}
      >
        <p className="tlw-txt-zh-head-5 tlw-px-1 md:tlw-txt-zh-head-6 md:tlw-text-txt-800 md:tlw-px-0">
          {title}
        </p>

        <Icon
          className="tlw-mr-3 md:tlw-hidden"
          name={isOpen ? 'Minus' : 'Plus'}
          size={20}
        />
      </div>

      {isMobile ? (
        <CSSTransition
          in={isOpen}
          timeout={500}
          unmountOnExit
          onEnter={handleEnter}
          onEntering={handleEntering}
          onExit={handleExit}
          onExited={handleExiting}
          nodeRef={elementRef}
        >
          <ul
            ref={elementRef}
            className="tlw-px-1 tlw-min-h-0 tlw-overflow-hidden md:tlw-px-0"
          >
            {items.map(({ mainHeading, link, linkTarget }) => (
              <li key={link} className="md:tlw-py-[3px]">
                <Link
                  href={link}
                  target={linkTarget}
                  className="tlw-txt-zh-head-8 tlw-text-txt-600 tlw-block tlw-w-fit tlw-duration-300 hover:tlw-text-txt-core"
                >
                  {mainHeading}
                </Link>
              </li>
            ))}
          </ul>
        </CSSTransition>
      ) : (
        <ul className="tlw-px-1 tlw-min-h-0 tlw-overflow-hidden md:tlw-px-0">
          {items.map(({ mainHeading, link, linkTarget }) => (
            <li key={link} className="md:tlw-py-[3px]">
              <Link
                href={link}
                target={linkTarget}
                className="tlw-txt-zh-head-8 tlw-text-txt-600 tlw-block tlw-w-fit tlw-duration-300 hover:tlw-text-txt-core"
              >
                {mainHeading}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
